"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DynamicButtonsFeature = void 0;
const Feature_1 = require("../Feature");
const mappers_1 = require("../utils/mappers");
const sorting_1 = require("../utils/sorting");
const types_1 = require("../utils/types");
const constants_1 = require("../utils/constants");
const SummaryCounter_1 = require("../SummaryCounter");
class DynamicButtonsFeature extends Feature_1.Feature {
    #config;
    #context;
    #xcreoHandler;
    #plan = [];
    #originalItems = [];
    #inheritedQueryParams;
    #uniqueMerchants = [];
    #cardEl;
    #silentMode;
    #moduleConfig;
    #htmlElementAttributeHandler;
    constructor(config, card, ctx, xcreoHandler) {
        super(card);
        this.#config = config;
        this.#cardEl = card.htmlElement;
        this.#silentMode = ctx.isSilentMode;
        this.#calculateOriginalItems();
        this.#xcreoHandler = xcreoHandler;
        const replaceOrUpdateAnyOriginal = this.#originalItems.find((item) => item.originalMode !== types_1.OriginalMode.NO_ACTION) !== undefined;
        this.#xcreoHandler.setupOptimizationReason(replaceOrUpdateAnyOriginal, this.#config.maxLinksPerCard);
        this.#addXcreoDataToOriginals();
        this.#inheritedQueryParams = this.#getInheritedQueryParamsFromOriginalUrls(this.#originalItems, this.#config.inheritedOriginalUrlQueryParams);
        this.#context = ctx;
        this.#htmlElementAttributeHandler = this.#context.htmlElementAttributeHandler;
        this.#moduleConfig = this.#context.moduleConfig;
    }
    populateInputData(dataProvider) {
        for (const originalItem of this.#originalItems) {
            if (!originalItem.originalUrl)
                continue;
            dataProvider.addOriginalUrl(originalItem.originalUrl, this.card, this.#config.maxLinksPerCard);
        }
    }
    get plan() {
        return this.#plan;
    }
    get moduleExecutionContext() {
        return this.#context;
    }
    #calculateOriginalItems() {
        const originalElements = this.#cardEl.querySelectorAll(this.#config.templateSelector) || [];
        this.#originalItems = [];
        let originalModeIndex = 0;
        for (let i = 0; i < originalElements.length; i++) {
            const originalElement = originalElements[i];
            const urlTargetElement = originalElement.tagName === "A" ? originalElement : originalElement.querySelector("a") || originalElement;
            const originalUrl = urlTargetElement.getAttribute("href") || undefined;
            if (this.#config.domainBlacklist.length > 0 && originalUrl) {
                const parsedUrl = new URL(originalUrl);
                if (this.#config.domainBlacklist.includes(parsedUrl.hostname))
                    continue;
            }
            const shouldSkipOriginalItem = this.#config.skipButtonClasses.find((className) => originalElement.classList.contains(className.replace(".", "")) || originalElement.querySelector(className));
            if (shouldSkipOriginalItem) {
                continue;
            }
            const originalMode = this.#config.originalButtonConfig?.length > 0 ? this.#config.originalButtonConfig[originalModeIndex] : types_1.OriginalMode.NO_ACTION;
            this.#originalItems.push({ originalElement, originalUrl, originalMode });
            if (this.#config.originalButtonConfig && originalModeIndex < this.#config.originalButtonConfig.length - 1) {
                originalModeIndex++;
            }
        }
    }
    #addXcreoDataToOriginals() {
        this.#originalItems.forEach((originalItem) => {
            this.#xcreoHandler.addXcreoToHtmlElement(originalItem.originalElement, types_1.ButtonMode.DEFAULT);
        });
    }
    #getInheritedQueryParamsFromOriginalUrls(originalItems, inheritedQueryParamNames) {
        if (!inheritedQueryParamNames)
            return new Map();
        let attributes = new Map();
        originalItems.forEach((originalItem) => {
            if (!originalItem.originalUrl)
                return;
            let originalUrl = new URL(originalItem.originalUrl);
            inheritedQueryParamNames.forEach((attributeKey) => {
                if (originalUrl.searchParams.has(attributeKey) && !attributes.has(attributeKey)) {
                    const attributeValue = originalUrl.searchParams.get(attributeKey);
                    attributes.set(attributeKey, attributeValue);
                }
            });
            if (inheritedQueryParamNames.length === attributes.size)
                return attributes;
        });
        return attributes;
    }
    apply(dataProvider, featureExecutionResults) {
        if (this.#htmlElementAttributeHandler.getAttribute(this.#cardEl, constants_1.PROCESSED_ATTRIBUTE))
            return false;
        this.#createPlan(dataProvider);
        if (this.#config.onPlanCreated && this.#plan.length > 0) {
            this.#config.onPlanCreated(this);
        }
        this.#htmlElementAttributeHandler.setAttribute(this.#cardEl, constants_1.PROCESSED_ATTRIBUTE, "true");
        return this.#executePlan();
    }
    #createPlan(dataProvider) {
        let buttonCount = this.#originalItems.length;
        this.#uniqueMerchants = [];
        this.#plan = [];
        const dataForUpdateOriginal = {};
        const uniqueAlternatives = [];
        this.#context.summaryCounters.increment(this.card.index, SummaryCounter_1.CountType.POSSIBLE_ADDITIONAL_ALTERNATIVES, this.#config.maxLinksPerCard - buttonCount);
        // Collect data from dataProvider
        if (this.#moduleConfig.useNewProductApi) {
            const cardRecommendationData = dataProvider.getCardData(this.card.index);
            if (!cardRecommendationData) {
                // No data for this card
                return false;
            }
            this.#originalItems.forEach((originalItem) => {
                const originalURL = originalItem.originalUrl;
                if (!originalURL || !URL.canParse(originalURL)) {
                    return;
                }
                const cardRecommendation = cardRecommendationData.original_urls[originalURL];
                if (!cardRecommendation || cardRecommendation.stock === null || cardRecommendation.price === null || cardRecommendation.stock === 0) {
                    return;
                }
                const mappedCardRecommendation = (0, mappers_1.mapUrlRecommendationToRecommendationDetails)(cardRecommendation);
                if (!this.#isProductValid(mappedCardRecommendation))
                    return;
                dataForUpdateOriginal[originalURL] = mappedCardRecommendation;
            });
            for (const key in cardRecommendationData.alternative_urls) {
                const cardRecommendation = cardRecommendationData.alternative_urls[key];
                if (cardRecommendation.stock === null || cardRecommendation.price === null || cardRecommendation.stock === 0) {
                    continue;
                }
                const mappedCardRecommendation = (0, mappers_1.mapUrlRecommendationToRecommendationDetails)(cardRecommendation);
                if (!this.#isProductValid(mappedCardRecommendation))
                    continue;
                uniqueAlternatives.push(mappedCardRecommendation);
            }
        }
        else {
            const uniqueAlternativeAdvertiserIds = new Set();
            this.#originalItems.forEach((originalItem) => {
                const originalURL = originalItem.originalUrl;
                if (!originalURL || !URL.canParse(originalURL)) {
                    return;
                }
                let alternativeProducts = dataProvider.getAlternativeProducts(originalURL);
                if (!alternativeProducts)
                    return;
                for (let alterIndex = 0; alterIndex < alternativeProducts.length; alterIndex++) {
                    const productDetails = alternativeProducts[alterIndex];
                    if (!productDetails.is_available || productDetails.price === null) {
                        continue;
                    }
                    if (productDetails.hasOwnProperty(constants_1.UPDATE_DATA_KEY)) {
                        const mappedProductDetails = (0, mappers_1.mapProductDetailsToRecommendationDetails)(productDetails);
                        if (!this.#isProductValid(mappedProductDetails))
                            continue;
                        dataForUpdateOriginal[originalURL] = mappedProductDetails;
                        continue;
                    }
                    if (uniqueAlternativeAdvertiserIds.has(productDetails.advertiser_id)) {
                        continue;
                    }
                    // ALTERNATIVE
                    const recommendationDetails = (0, mappers_1.mapProductDetailsToRecommendationDetails)(productDetails);
                    if (!this.#isProductValid(recommendationDetails))
                        continue;
                    uniqueAlternatives.push(recommendationDetails);
                    if (recommendationDetails.advertiser_id) {
                        uniqueAlternativeAdvertiserIds.add(recommendationDetails.advertiser_id);
                    }
                }
            });
        }
        if (this.#shouldAddFakeAlternative())
            uniqueAlternatives.push(this.#createFakeAlternatives());
        uniqueAlternatives.forEach((recommendationDetails) => {
            if (this.#config.mapMerchantName) {
                recommendationDetails.merchant_name = this.#config.mapMerchantName(recommendationDetails.merchant_name);
            }
        });
        this.#originalItems.forEach((originalItem) => {
            if (!originalItem.originalUrl)
                return;
            const recommendationDetails = dataForUpdateOriginal[originalItem.originalUrl];
            if (!recommendationDetails)
                return;
            if (this.#config.mapMerchantName) {
                recommendationDetails.merchant_name = this.#config.mapMerchantName(recommendationDetails.merchant_name);
            }
            if (originalItem.originalMode !== types_1.OriginalMode.REPLACE) {
                const merchantInfo = this.#createMerchantInfo(recommendationDetails);
                this.#uniqueMerchants.push(merchantInfo);
            }
            if (originalItem.originalMode === types_1.OriginalMode.UPDATE) {
                this.#context.summaryCounters.increment(this.card.index, SummaryCounter_1.CountType.ORIGINAL_LINKS_POSSIBLE_TO_BE_UPDATED);
            }
            else if (originalItem.originalMode === types_1.OriginalMode.REPLACE) {
                this.#context.summaryCounters.increment(this.card.index, SummaryCounter_1.CountType.ORIGINAL_LINKS_POSSIBLE_TO_BE_REPLACED);
            }
        });
        (0, sorting_1.sortByProperty)(uniqueAlternatives, this.#config.sortBy ?? "score", "DESC");
        // Create plan for original buttons
        this.#originalItems.forEach((originalItem) => {
            if (!originalItem.originalUrl || originalItem.originalMode === types_1.OriginalMode.NO_ACTION) {
                return;
            }
            switch (originalItem.originalMode) {
                case types_1.OriginalMode.UPDATE:
                    if (dataForUpdateOriginal[originalItem.originalUrl]) {
                        this.#addPlanItem(types_1.ButtonMode.UPDATE, originalItem.originalElement, originalItem.originalUrl, dataForUpdateOriginal[originalItem.originalUrl]);
                    }
                    break;
                case types_1.OriginalMode.REPLACE:
                    if (uniqueAlternatives.length > 0) {
                        this.#addPlanItem(types_1.ButtonMode.REPLACE, originalItem.originalElement, originalItem.originalUrl, uniqueAlternatives.shift());
                    }
                    break;
            }
        });
        // Create plan for alternatives
        const originalItem = this.#originalItems.find((item) => item.originalUrl !== undefined);
        if (!originalItem) {
            this.#context.summaryBuilder.appendClientWarningMessages("Links and Buttons - No valid original url found");
            return;
        }
        while (buttonCount < this.#config.maxLinksPerCard && uniqueAlternatives.length > 0) {
            const productDetails = uniqueAlternatives.shift();
            const success = this.#addPlanItem(types_1.ButtonMode.ALTERNATIVE, originalItem.originalElement, originalItem.originalUrl ?? "", productDetails);
            if (success) {
                buttonCount++;
            }
        }
    }
    #addPlanItem(buttonMode, originalElement, originalURL, productDetails) {
        const url = new URL(productDetails.urls.affiliated_url);
        this.#xcreoHandler.appendURLWithXcreo(url, buttonMode);
        if (this.#inheritedQueryParams.size !== 0 && (buttonMode === types_1.ButtonMode.ALTERNATIVE || buttonMode === types_1.ButtonMode.REPLACE)) {
            this.#inheritedQueryParams.forEach((value, key) => {
                url.searchParams.set(key, value);
            });
        }
        const newUrl = url.toString();
        let planItem = {
            action: buttonMode,
            input: {
                originalElement: originalElement,
                originalUrl: originalURL,
            },
            output: {
                newUrl: newUrl,
            },
            productDetails: productDetails,
        };
        let anchorElement;
        let merchantInfo;
        if (buttonMode === types_1.ButtonMode.ALTERNATIVE || buttonMode === types_1.ButtonMode.REPLACE) {
            const merchantInfo = this.#createMerchantInfo(productDetails);
            const matchingUniqueMerchant = this.#uniqueMerchants.find((uniqueMerchant) => uniqueMerchant.merchantDomain === merchantInfo.merchantDomain ||
                (uniqueMerchant.merchantId !== "" && merchantInfo.merchantId !== "" && uniqueMerchant.merchantId === merchantInfo.merchantId) ||
                uniqueMerchant.merchantName === merchantInfo.merchantName);
            // Already existing merchant
            if (matchingUniqueMerchant) {
                return false;
            }
            planItem.output.newMerchantDomain = merchantInfo.merchantDomain;
        }
        if (buttonMode === types_1.ButtonMode.UPDATE || buttonMode === types_1.ButtonMode.REPLACE) {
            anchorElement = originalElement?.getAttribute("href") ? originalElement : originalElement?.querySelector("a");
            if (!anchorElement) {
                this.#context.summaryBuilder.appendClientWarningMessages("Links and Buttons - Anchor element or link value not found in original template");
                return false;
            }
            planItem.input.originalAnchorElement = anchorElement;
            planItem.output.newOriginalAnchorInnerTemplate = this.#getNewOriginalInnerTemplate(productDetails, newUrl);
        }
        if (buttonMode === types_1.ButtonMode.ALTERNATIVE) {
            planItem.output.newAlternativeTemplate = this.#createButtonTemplate(productDetails, newUrl);
        }
        this.#plan.push(planItem);
        if (merchantInfo) {
            this.#uniqueMerchants.push(merchantInfo);
        }
        return true;
    }
    #executePlan() {
        if (this.#plan.length === 0)
            return false;
        this.#plan.forEach((planItem) => {
            planItem.action === types_1.ButtonMode.ALTERNATIVE ? this.#addButton(planItem) : this.#changeOriginalButton(planItem);
        });
        this.#setupDebugMode();
        return true;
    }
    #setupDebugMode() {
        if (this.card.module.debugMode && !this.#silentMode) {
            // SHOW MERCHANT LIST for debug on cards
            const merchantListHtml = document.createElement("ul");
            merchantListHtml.classList.add(constants_1.DEBUG_LIST_CLASS);
            this.#uniqueMerchants.forEach((merchantInfo) => {
                const merchantDomainElement = document.createElement("li");
                merchantDomainElement.innerHTML = merchantInfo.merchantDomain + " - " + merchantInfo.merchantId + " - " + merchantInfo.merchantName;
                merchantListHtml.appendChild(merchantDomainElement);
            });
            this.#cardEl.appendChild(merchantListHtml);
            this.#cardEl.addEventListener("mouseover", () => {
                merchantListHtml.style.opacity = "1";
            });
            this.#cardEl.addEventListener("mouseleave", () => {
                merchantListHtml.style.opacity = "0";
            });
        }
    }
    #createMerchantInfo(recommendationDetails) {
        return {
            merchantDomain: this.#getMerchantDomain(recommendationDetails.urls.affiliated_url),
            merchantId: recommendationDetails?.advertiser_id || "",
            merchantName: this.#normalizeString(recommendationDetails.merchant_name),
        };
    }
    #normalizeString(value) {
        if (value === undefined || value === null)
            return "";
        return value.replace(/\s+/g, " ").trim().toLowerCase();
    }
    #getMerchantDomain(url) {
        try {
            if (!URL.canParse(url) || url === "#")
                return "";
            const urlInput = url.includes(constants_1.SKIMLINKS_LINK_FRAGMENT) ? new URL(url).searchParams.get("url") || "" : "";
            const urlProduct = decodeURIComponent(urlInput || url);
            const parsedUrl = new URL(urlProduct);
            return parsedUrl.hostname;
        }
        catch (error) {
            this.#context.summaryBuilder.appendClientWarningMessages("Links and buttons - Error occurred while getting merchant domain:" + error?.toString());
            return "";
        }
    }
    #getNewOriginalInnerTemplate(productDetails, newUrl) {
        let newInnerTemplate = this.#config.originalButton.anchorInnerTemplate;
        if (this.#config.originalButton.anchorInnerTemplateForDiscount &&
            productDetails.original_price &&
            productDetails.original_price > productDetails.price) {
            newInnerTemplate = this.#config.originalButton.anchorInnerTemplateForDiscount;
        }
        return this.#resolveTemplate(newInnerTemplate, productDetails, newUrl);
    }
    #changeOriginalButton(planItem) {
        const mode = planItem.action;
        const originalURL = planItem.input.originalUrl;
        const originalElement = planItem.input.originalElement;
        const originalAnchorElement = planItem.input.originalAnchorElement;
        const newUrl = planItem.output.newUrl;
        const newOriginalAnchorInnerTemplate = planItem.output.newOriginalAnchorInnerTemplate;
        const productDetails = planItem.productDetails;
        if (!originalElement) {
            this.#context.summaryBuilder.appendClientWarningMessages("Links and Buttons - Original element not found in plan");
            return false;
        }
        if (!this.#silentMode) {
            originalAnchorElement.innerHTML = newOriginalAnchorInnerTemplate;
        }
        if (mode === types_1.ButtonMode.REPLACE) {
            if (!this.#silentMode) {
                originalElement?.setAttribute("href", newUrl);
            }
            this.#context.summaryCounters.increment(this.card.index, SummaryCounter_1.CountType.ORIGINAL_LINKS_REPLACED);
            this.#xcreoHandler.removeXcreoFromHtmlElement(originalAnchorElement);
            this.#htmlElementAttributeHandler.setAttribute(originalElement, constants_1.REPLACED_ATTRIBUTE, "true");
        }
        else {
            this.#context.summaryCounters.increment(this.card.index, SummaryCounter_1.CountType.ORIGINAL_LINKS_UPDATED);
            this.#xcreoHandler.addXcreoToHtmlElement(originalAnchorElement, mode);
            this.#htmlElementAttributeHandler.setAttribute(originalElement, constants_1.UPDATED_ATTRIBUTE, "true");
        }
        const eventData = this.#createEventData(mode, types_1.EventType.DOM, originalURL, newUrl, productDetails.advertiser_id);
        this.#context.domEventSender.sendEvent(eventData);
        if (!this.#silentMode) {
            this.#context.visibilityObserver.observe(originalElement, eventData, this.#config.onButtonVisible ? () => this.#config.onButtonVisible?.(originalAnchorElement, mode, productDetails) : undefined);
        }
        if (this.#config.onDomChanged) {
            this.#config.onDomChanged(originalAnchorElement, mode, productDetails);
        }
        return true;
    }
    #addButton(planItem) {
        const newAlternativeTemplate = planItem.output.newAlternativeTemplate;
        const originalUrl = planItem.input.originalUrl;
        const productDetails = planItem.productDetails;
        const newUrl = planItem.output.newUrl;
        const newButton = this.#createUiElement(newAlternativeTemplate);
        this.#htmlElementAttributeHandler.setAttribute(newButton, constants_1.ALTERNATIVE_ATTRIBUTE, "true");
        if (!this.#silentMode) {
            this.#cardEl.appendChild(newButton);
        }
        this.#context.summaryCounters.increment(this.card.index, SummaryCounter_1.CountType.DISPLAYED_ADDITIONAL_ALTERNATIVES);
        const eventData = this.#createEventData(types_1.ButtonMode.ALTERNATIVE, types_1.EventType.DOM, originalUrl, newUrl, productDetails.advertiser_id);
        this.#context.domEventSender.sendEvent(eventData);
        this.#context.visibilityObserver.observe(newButton, eventData, this.#config.onButtonVisible ? () => this.#config.onButtonVisible?.(newButton, planItem.action, productDetails) : undefined);
        if (this.#config.onDomChanged) {
            this.#config.onDomChanged(newButton, planItem.action, productDetails);
        }
    }
    #createButtonTemplate(productDetails, XcreoURL) {
        const templateConfig = this.#config.alternativeButton;
        let template = templateConfig.template;
        if (templateConfig.hasOwnProperty("templateForDiscount") &&
            productDetails.original_price &&
            productDetails.original_price > productDetails.price) {
            template = templateConfig.templateForDiscount;
        }
        return this.#resolveTemplate(template, productDetails, XcreoURL);
    }
    #resolveTemplate(template, productDetails, url) {
        return template
            .replace(/{{url}}/g, url || "")
            .replace(/{{merchantName}}/g, productDetails.merchant_name)
            .replace(/{{currency}}/g, this.#context.priceFormatter.getCurrencySymbol(productDetails.currency))
            .replace(/{{originalPriceWithoutCurrency}}/g, this.#context.priceFormatter.getFormattedPrice(productDetails.original_price))
            .replace(/{{currentPriceWithoutCurrency}}/g, this.#context.priceFormatter.getFormattedPrice(productDetails.price));
    }
    #createUiElement(template) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(template, "text/html");
        return doc.body.firstChild;
    }
    #createEventData(mode, type, originalURL, alternativeURL, advertiserId) {
        let pageUrl = new URL(window.location.href);
        return {
            mode: mode,
            type: type,
            input_url: originalURL,
            output_url: alternativeURL,
            browser_time: new Date().toString(),
            browser_agent: window.navigator.userAgent,
            impression_id: this.#context.impressionId,
            output_advertiser_id: advertiserId,
            publisher_id: this.#moduleConfig.skimlinksPublisherId,
            publisher_domain_id: this.#moduleConfig.skimlinksPublisherDomainId,
            page_url: window.location.href,
            normalized_page_url: pageUrl.hostname + pageUrl.pathname,
            card_index: this.card.index,
            execution_id: this.#context.executionId,
            section_name: this.card.sectionConfig.name,
            build_no: this.card.module.buildNo,
            silent_mode: this.#silentMode,
        };
    }
    #createFakeAlternatives() {
        const fakeAlternative = {
            advertiser_id: "fake_test_id_123",
            currency: "GBP",
            is_available: true,
            merchant_name: "fake_merchant_name_1",
            price: 1234,
            urls: {
                affiliated_url: "https://www.fake_affiliated_url_1.com",
                product_url: "https://www.fake_product_url_1.com",
            },
            input_url: "https://www.fake_input_url_1.com",
            original_price: 1234,
            ecpc: 1234,
            description: "fake_description_1",
            product_images: "fake_product_images_1",
            product_name: "fake_product_name_1",
            product_brand: "fake_product_brand_1",
            sku: "fake_sku_'",
            score: 1234,
        };
        return fakeAlternative;
    }
    #shouldAddFakeAlternative() {
        return window._tblGravity?.useFakeAlternative;
    }
    #isProductValid(productDetails) {
        if (!this.#config.validateProduct) {
            return true;
        }
        const validationResult = this.#config.validateProduct(productDetails);
        const isSuccessful = validationResult[0];
        const errors = validationResult[1];
        if (!isSuccessful) {
            errors.forEach((error) => {
                this.#context.summaryBuilder.appendClientWarningMessages(error);
            });
            return false;
        }
        return true;
    }
}
exports.DynamicButtonsFeature = DynamicButtonsFeature;
